<template>
  <div class="app-container">
    <Header :show-scroll-progress="true" />
    <div class="blog-content-container">
      <div :class="mainContentClass">
        <BlogHeader
          :title="title"
          :subheading="subheading"
          :date="date"
          :author="author"
          :author-avatar="authorAvatar"
          :reading-time="readingTime"
          :hero-image="heroImage"
        />

        <div class="remote-inspections-blog">
          <section class="blog-section">
            <p>
              For years, remote inspections have lived in a strange gray zone.
              Everyone claims they want them—faster, cheaper, safer—but the
              moment the word "remote" appears in the workflow, doubts quietly
              creep in. Did the operator actually cover the entire scope? Were
              the critical areas checked closely enough? Is the footage
              real-time or stitched together? Will the final report miss
              something that forces a second site visit anyway?
            </p>
            <p>
              These doubts are not irrational. Clients in industrial, solar,
              telecom, and utility environments carry real operational risk, and
              a PDF report delivered two days later rarely makes anyone feel
              reassured. That's why so many teams still insist on physical
              presence even when drones should've eliminated that need years
              ago.
            </p>
            <p>
              The turning point happens when the inspection stops being a black
              box and starts being a shared experience. When clients can join
              the session live, talk through what they're seeing, observe how
              the operator covers the checklist, and watch findings get marked
              the moment they appear, the "remote" part doesn't feel remote
              anymore. It feels like being on-site—minus the travel and safety
              hazards.
            </p>
          </section>

          <section class="blog-section">
            <h2>What clients are really unsure about</h2>
            <p>
              If you listen closely, clients don't actually care whether the
              operator is on the roof, in the field, or hundreds of kilometers
              away. They care about traceability. They care about proof of
              coverage. They care about knowing that somebody didn't miss the
              one thing that later becomes a multi-hour outage.
            </p>
            <p>
              Most of the skepticism around remote inspections comes from the
              gaps between the camera and the paper. Those gaps disappear the
              moment the client can see the feed, hear the operator, and watch
              critical steps get completed in real time. No guessing. No hoping.
              No "did we actually check this corner?" conversations.
            </p>
          </section>

          <section class="blog-section">
            <h2>Live checklisting changes the dynamic entirely</h2>
            <p>
              This is one of those cases where a simple feature has a massive
              emotional impact. When clients can watch you tick off inspection
              items during the flight, they immediately feel the process
              tightening up. Every checked item becomes visible to everyone in
              the session. Every section of the asset gets acknowledged. It's a
              small thing on paper, but in practice it creates a shared sense of
              confidence that a PDF can't replicate.
            </p>
            <p class="highlight-text">
              You're no longer asking them to trust that the job was done.
            </p>
            <p class="highlight-text">They are seeing it get done.</p>
          </section>

          <section class="blog-section">
            <h2>
              Annotations and evidence markers make the session verifiable
            </h2>
            <p>
              Clients love clarity. They love knowing that something spotted at
              10:41 a.m. is preserved, referenced, and available later—without
              digging through a 40-minute video. Evidence markers solve this
              elegantly. Instead of vague notes like "possible corrosion on
              north panel," you drop a marker live, label it, and move on. The
              moment is saved, time-linked, and easy to export.
            </p>
            <p>
              These micro-interactions do more than make reporting easier. They
              create a transparent trail clients can sign off on. For industries
              where things can get political—solar farms, telecom towers,
              industrial facilities—this kind of record-keeping is gold.
            </p>
          </section>

          <section class="blog-section">
            <h2>Reports that assemble themselves</h2>
            <p>
              This is where remote inspections stop feeling like a compromise
              and start feeling superior. When all the moments that matter have
              already been captured, tagged, and organized during the flight,
              the final report isn't something you build manually. It's
              something that emerges from the session log.
            </p>
            <p>Clients don't wait days.</p>
            <p>Operators don't spend nights editing.</p>
            <p>
              And approvals—surprisingly—happen faster because everything they
              need is already there in clean, bite-sized evidence clips.
            </p>
            <p>
              In many cases, teams cut 40% off their approval cycles simply
              because nobody needs to re-interpret what happened. It's all
              there.
            </p>
          </section>

          <section class="blog-section">
            <h2>SLA honesty: fewer site revisits, fewer excuses</h2>
            <p>
              There's a recurring pattern among operators who switch to live,
              collaborative inspections: they stop over-promising. Not because
              they've become conservative, but because the workflow itself
              becomes predictable. When you can cover a full checklist in one
              session, with the client inside the flight, there's very little
              ambiguity left.
            </p>
            <p>And the measurable result?</p>
            <p class="highlight-text">
              Most teams see 25–50% fewer site revisits within the first few
              months.
            </p>
            <p>
              Remote inspections stop being a gamble and start being dependable.
            </p>
          </section>

          <section class="blog-section">
            <h2>This is where Skyhost fits naturally</h2>
            <p>
              Skyhost wasn't built as a streaming toy. It was designed to turn
              remote inspections into a format clients can trust without
              hesitation:
            </p>
            <ul class="features-list">
              <li>Live annotations they can watch as you make them</li>
              <li>Visual checklists that update for all participants</li>
              <li>Session logs that assemble into reports automatically</li>
              <li>Evidence clips linked directly to inspection moments</li>
            </ul>
            <p>
              Nothing fancy for the sake of being fancy—just the things that
              matter when the job needs to be correct, defensible, and approved
              quickly.
            </p>
          </section>

          <section class="blog-section" style="border-bottom: none">
            <h2>Try a mock run once—clients will get it immediately</h2>
            <p>
              If you've never done a live, collaborative inspection before, run
              a mock session. Invite an engineer or a manager for 10 minutes.
              Walk through a small checklist. Drop an annotation. End the call.
            </p>
            <p>
              You'll notice it instantly—this doesn't feel like "remote work."
            </p>
            <p>It feels like the new standard.</p>
            <p><strong>And it should be.</strong></p>
            <p>
              Ready to transform your remote inspections? Get started with
              <a href="https://skyhost.ai" target="_blank" class="blog-link"
                >Skyhost</a
              >
              today.
            </p>
          </section>
        </div>

        <BlogFooter
          :tags="tags"
          :title="title"
          :current-page-url="currentPageUrl"
          :related-blogs="relatedBlogs"
          @navigate-to-blog="navigateToBlog"
          @navigate-to-blogs="navigateToBlogs"
        />
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import { isMobile } from "@/utils/helper";
import { computed } from "vue";
import Header from "@/components/organisms/Header.vue";
import Footer from "@/components/Footer.vue";
import BlogHeader from "@/components/organisms/BlogHeader.vue";
import BlogFooter from "@/components/organisms/BlogFooter.vue";
import { blogsRegistry } from "@/data/blogRegistry";

export default {
  name: "RemoteInspectionsTrustBlog",
  components: {
    Header,
    Footer,
    BlogHeader,
    BlogFooter,
  },
  data() {
    const blogId = "remote-inspections-trust";
    const blog = blogsRegistry[blogId];

    return {
      id: blogId,
      title: blog.title,
      subheading: blog.subheading,
      date: blog.date,
      author: blog.author,
      authorAvatar: blog.authorAvatar,
      image: blog.image,
      heroImage: blog.image,
      excerpt: blog.excerpt,
      tags: blog.tags,
      relatedBlogs: [],
    };
  },
  computed: {
    isMobileLayout() {
      return isMobile();
    },
    currentPageUrl() {
      return `${window.location.origin}${this.$route.fullPath}`;
    },
    readingTime() {
      const wordCount =
        this.title.split(" ").length +
        (this.excerpt?.split(" ").length || 0) +
        2200;
      return Math.ceil(wordCount / 200);
    },
  },
  methods: {
    navigateToBlog(blogId) {
      this.$router.push({
        path: `/blog/${blogId}`,
      });
    },
    navigateToBlogs() {
      this.$router.push({
        path: "/blogs",
      });
    },
  },
  created() {
    // Get related blogs (excluding current blog)
    const allOtherBlogs = Object.values(blogsRegistry).filter(
      (blog) => blog.id !== this.id
    );

    // First, try to get blogs with matching tags
    const matchingTagBlogs = allOtherBlogs.filter((blog) =>
      blog.tags.some((tag) => this.tags.includes(tag))
    );

    // If we have matching tag blogs, use them, otherwise use any other blogs
    const relatedBlogs =
      matchingTagBlogs.length > 0 ? matchingTagBlogs : allOtherBlogs;

    // Take up to 2 blogs
    this.relatedBlogs = relatedBlogs.slice(0, 2);
  },
  setup() {
    const mainContentClass = computed(() => {
      return isMobile() ? "mobile-blog-content" : "web-blog-content";
    });
    return {
      mainContentClass,
    };
  },
};
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #fff;
  color: #333;
}

.blog-content-container {
  margin-top: 5rem;
}

.web-blog-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

.mobile-blog-content {
  padding: 1.5rem;
}
.remote-inspections-blog {
  font-family: "Charter", "Georgia", "Cambria", serif;
  line-height: 1.8;
  color: #333;
  font-size: 21px;
}

.blog-section {
  margin-bottom: 4rem;
  padding: 2rem 0;
  border-bottom: 1px solid #f0f0f0;
}

h2 {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: #000;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

h3 {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: #000;
  font-weight: 600;
  letter-spacing: -0.01em;
}

p {
  margin-bottom: 1.5rem;
  font-size: 21px;
  line-height: 1.8;
  font-family: "Charter", "Georgia", "Cambria", serif;
}

.highlight-text {
  font-size: 1.15rem;
  color: #000;
  font-weight: 500;
  margin: 1.5rem 0;
  padding-left: 1rem;
  border-left: 3px solid #1565c0;
}

.features-list {
  padding-left: 1.5rem;
  margin-bottom: 1.5rem;
  font-family: "Charter", "Georgia", "Cambria", serif;
  font-size: 21px;
}

.features-list li {
  margin-bottom: 0.8rem;
  line-height: 1.8;
}

.blog-link {
  color: #1565c0;
  text-decoration: underline;
}

@media (max-width: 768px) {
  .web-blog-content {
    padding: 1.5rem;
  }

  h2 {
    font-size: 1.75rem;
  }

  .highlight-text {
    font-size: 1.05rem;
    padding-left: 0.8rem;
  }
}
</style>
